/**
 * Engagement model for the phone search bar.
 *
 * One question: is the user still *composing*? — keeps the two-row composer
 * capsule mounted so a footer control can't vanish from under a mid-tap finger.
 * Static content counts: a draft or an attachment (photo, link, note) means the
 * query is still being assembled even with the keyboard closed.
 *
 * This file used to answer a second question — "is the keyboard up?" — which
 * drove the hero's vertical position. That whole raise is gone: the bar keeps
 * its resting position on every layout, and focusing it is left to the browser's
 * native behaviour. Nothing here moves the page any more.
 *
 * Pure functions with no React import so they can be unit-tested under the
 * repo's node-environment jest.
 */

/** Signals that mean "still composing" — keeps the composer capsule open. */
export type ComposerHoldSignals = {
  searchInput: string;
  /** A photo, link or note waits in the bar to be sent. */
  hasAttachments: boolean;
  /** The "+" menu is open: its link and note fields take focus from the textarea by design. */
  isAttachMenuOpen: boolean;
  /** The budget chip's field is open: it takes focus from the textarea by design. */
  isEditingBudget: boolean;
  showRegionSelector: boolean;
  isVoiceListening: boolean;
  isSpeechProcessing: boolean;
  isImagePickerOpen: boolean;
  showHelpOptions: boolean;
};

export type PanelKeyboardSignals = {
  isPhoneLayout: boolean;
  topMenu: boolean;
  messageIsNotEmpty: boolean;
  isLoading: boolean;
  showRegionSelector?: boolean;
};

/**
 * Recording clears searchInput (so the text guard alone would fail) yet the user
 * is mid-interaction; the native image picker and the budget field steal
 * document focus the same way.
 */
export const shouldHoldComposerOpen = (s: ComposerHoldSignals): boolean =>
  s.searchInput.length > 0 ||
  s.hasAttachments ||
  s.isAttachMenuOpen ||
  s.isEditingBudget ||
  s.showRegionSelector ||
  s.isVoiceListening ||
  s.isSpeechProcessing ||
  s.isImagePickerOpen ||
  s.showHelpOptions;

/**
 * The region panel grows upward from the bar's top edge and wants most of a
 * phone screen, which an open keyboard is busy occupying. Dismiss it on the
 * panel's opening edge. This is a blur and nothing else — no scroll, no layout
 * change — and it is a no-op when the field wasn't focused to begin with.
 *
 * Post-search it only fires for the region picker: messages already exist and
 * the check-region request may still be loading, but the map needs the screen.
 */
export const shouldDismissKeyboardForPanel = (s: PanelKeyboardSignals): boolean => {
  if (!s.isPhoneLayout || s.topMenu) return false;
  if (s.showRegionSelector) return true;
  return !s.messageIsNotEmpty && !s.isLoading;
};
